import _ from 'lodash';

import {
  AiProcessOwnerKind,
  AiProcessRunsSignalREventType,
  AiProcessRunsSignalRMessageType,
} from 'constants/aiProcessRuns';

import {
  executionUpsertAction,
  formFieldSetAction,
  formStatusChangedAction,
  messageAppendAction,
  setStepPendingQuestionAction,
  setStepPendingToolCallAction,
  stepUpsertAction,
} from 'store/aiProcessRuns/actions';

const executionStatusByEvent = {
  [AiProcessRunsSignalREventType.EXECUTION_STARTED]: 'Running',
  [AiProcessRunsSignalREventType.EXECUTION_COMPLETED]: 'Completed',
  [AiProcessRunsSignalREventType.EXECUTION_FAILED]: 'Failed',
  [AiProcessRunsSignalREventType.EXECUTION_CANCELLED]: 'Cancelled',
  [AiProcessRunsSignalREventType.EXECUTION_AWAITING_CONFIRMATION]: 'AwaitingConfirmation',
};

// DEV-3325: events that terminally end an execution. On these, if the backend
// payload didn't carry a completedAt, we stamp the event arrival time so the
// run-detail header switches from the live ticking "Duration" to a frozen
// "Completed" — otherwise status flips to Completed/Failed while completedAt
// stays null and the duration keeps climbing forever.
const TerminalExecutionEventTypes = new Set([
  AiProcessRunsSignalREventType.EXECUTION_COMPLETED,
  AiProcessRunsSignalREventType.EXECUTION_FAILED,
  AiProcessRunsSignalREventType.EXECUTION_CANCELLED,
]);

const stepStatusByEvent = {
  [AiProcessRunsSignalREventType.STEP_STARTED]: 'Running',
  [AiProcessRunsSignalREventType.STEP_COMPLETED]: 'Completed',
  [AiProcessRunsSignalREventType.STEP_FAILED]: 'Failed',
  [AiProcessRunsSignalREventType.STEP_AWAITING_CONFIRMATION]: 'AwaitingConfirmation',
  [AiProcessRunsSignalREventType.STEP_AWAITING_USER_INPUT]: 'AwaitingUserInput',
};

// Older runs (and older servers) send these as null. Dropping a nil value keeps
// it from overwriting a graph already loaded over REST when the step is merged.
const RUN_GRAPH_FIELDS = ['wave', 'dependsOnStepExecutionIds'];

const isPlainObject = value => _.isObject(value) && !_.isArray(value);

// Real backend events are flat (`{ executionId, processId, ... }`). The legacy
// storyMock fixtures nest under `{ execution: { id, ... } }`; we accept both so
// snapshot tests / Storybook still drive the controller.
const extractExecutionFromPayload = payload => {
  if (!isPlainObject(payload)) {
    return null;
  }

  const nested = isPlainObject(payload.execution) ? payload.execution : null;
  const id = nested?.id ?? payload.executionId ?? payload.id ?? null;

  if (!id) {
    return null;
  }

  return { ...payload, ...(nested ?? {}), id };
};

const extractStepFromPayload = payload => {
  if (!isPlainObject(payload)) {
    return null;
  }

  const nested =
    (isPlainObject(payload.step) && payload.step) ||
    (isPlainObject(payload.stepExecution) && payload.stepExecution) ||
    null;
  const id =
    nested?.id ?? payload.stepExecutionId ?? payload.aiProcessStepExecutionId ?? payload.id ?? null;

  if (!id) {
    return null;
  }

  const executionId =
    nested?.executionId ??
    nested?.aiProcessExecutionId ??
    payload.executionId ??
    payload.aiProcessExecutionId ??
    null;

  return _.omitBy(
    { ...payload, ...(nested ?? {}), id, executionId },
    (value, key) => RUN_GRAPH_FIELDS.includes(key) && _.isNil(value),
  );
};

// Tool/plan/response_token events almost never carry a top-level id, but they
// all reference a step execution. Synthesize a stable per-event id from any
// available correlator so the reducer can store the event without collision —
// without an id the reducer drops the dispatch entirely.
const extractMessageFromPayload = (payload, eventName) => {
  if (!isPlainObject(payload)) {
    return null;
  }

  const stepExecutionId =
    payload.stepExecutionId ?? payload.aiProcessStepExecutionId ?? payload.stepId ?? null;

  if (!stepExecutionId) {
    return null;
  }

  const nested = isPlainObject(payload.message) ? payload.message : null;
  const correlator =
    nested?.id ??
    payload.messageId ??
    payload.toolCallId ??
    payload.toolExecutionId ??
    payload.planId ??
    payload.planStepId ??
    payload.id ??
    null;
  const sequence =
    payload.sequence ?? payload.sequenceNumber ?? payload.tokenIndex ?? payload.timestamp ?? null;
  const messageId =
    correlator ??
    `${eventName}-${stepExecutionId}${sequence != null ? `-${sequence}` : `-${Date.now()}`}`;

  return {
    stepExecutionId,
    message: { ...payload, ...(nested ?? {}), id: messageId },
  };
};

// Form events are flat (`{ formId, executionId, fieldKey, ... }`). We key the
// store slice by executionId — the same anchor the drawer's REST load uses — so
// the only hard requirement is an executionId.
const extractFormFieldEventFromPayload = payload => {
  if (!isPlainObject(payload)) {
    return null;
  }

  const executionId = payload.executionId ?? payload.aiProcessExecutionId ?? null;

  if (!executionId || !payload.fieldKey) {
    return null;
  }

  return {
    executionId,
    event: {
      fieldKey: payload.fieldKey,
      value: payload.value,
      provenanceSource: payload.provenanceSource,
      confidence: payload.confidence,
      reviewState: payload.reviewState,
    },
  };
};

const extractFormStatusEventFromPayload = payload => {
  if (!isPlainObject(payload)) {
    return null;
  }

  const executionId = payload.executionId ?? payload.aiProcessExecutionId ?? null;

  if (!executionId || !payload.status) {
    return null;
  }

  return { executionId, status: payload.status };
};

const resolveOwnerIdFromPayload = (execution, payload) => {
  const candidate =
    execution?.ownerId ??
    payload?.ownerId ??
    execution?.entityId ??
    payload?.entityId ??
    execution?.targetId ??
    payload?.targetId ??
    execution?.loanApplicationId ??
    payload?.loanApplicationId ??
    null;

  if (isPlainObject(candidate)) {
    return candidate.applicationId ?? candidate.id ?? null;
  }

  return candidate;
};

const OWNER_GROUP_MESSAGE_TYPES = {
  [AiProcessOwnerKind.LOAN_APPLICATION]: {
    join: AiProcessRunsSignalRMessageType.JOIN_LOAN_APPLICATION,
    leave: AiProcessRunsSignalRMessageType.LEAVE_LOAN_APPLICATION,
  },
  [AiProcessOwnerKind.SUB_COMPANY]: {
    join: AiProcessRunsSignalRMessageType.JOIN_SUB_COMPANY,
    leave: AiProcessRunsSignalRMessageType.LEAVE_SUB_COMPANY,
  },
  [AiProcessOwnerKind.RATE_SHEET_UPLOAD]: {
    join: AiProcessRunsSignalRMessageType.JOIN_RATE_SHEET_UPLOAD,
    leave: AiProcessRunsSignalRMessageType.LEAVE_RATE_SHEET_UPLOAD,
  },
};

class SignalRAiProcessRunsController {
  #connection;
  #dispatch;
  #logger;
  #joinedOwners;
  #joinedExecutionIds;
  #pendingExecutionIds;

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
    this.#joinedOwners = new Map();
    this.#joinedExecutionIds = new Set();
    this.#pendingExecutionIds = new Set();
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error(
        '[AiProcessRuns SignalR] Trying to use connection without active instance',
      );
    }

    return this.#connection;
  }

  connect({ dispatch }) {
    if (!dispatch) {
      this.#logger.error(
        '[AiProcessRuns SignalR] Dispatch is required for initializing controller',
      );

      return;
    }

    if (this.#dispatch) {
      this.#dispatch = dispatch;

      return;
    }

    this.#dispatch = dispatch;
    this.#startListeners();
  }

  disconnect() {
    if (!this.#dispatch) {
      return;
    }

    this.#stopListeners();
    this.#joinedOwners.clear();
    this.#joinedExecutionIds.clear();
    this.#pendingExecutionIds.clear();
    this.#dispatch = null;
  }

  getTrackedExecutionIds() {
    return [...new Set([...this.#joinedExecutionIds, ...this.#pendingExecutionIds])];
  }

  async resubscribe() {
    const owners = [...this.#joinedOwners.entries()];
    const executions = new Set([...this.#joinedExecutionIds, ...this.#pendingExecutionIds]);

    await Promise.all([
      ...owners.map(([ownerId, { ownerKind, companyId }]) =>
        this.joinOwner(ownerKind, companyId, ownerId),
      ),
      ...[...executions].map(executionId => this.joinExecution(executionId)),
    ]);
  }

  async joinOwner(ownerKind, companyId, ownerId) {
    if (!companyId || !ownerId) {
      return;
    }

    const groupMessageTypes = OWNER_GROUP_MESSAGE_TYPES[ownerKind];

    if (!groupMessageTypes) {
      this.#logger.error('[AiProcessRuns SignalR] Unsupported owner kind for join', ownerKind);

      return;
    }

    if (!this.#connection?.isConnected) {
      this.#logger.warn('[AiProcessRuns SignalR] Skipping join — connection not active', ownerId);

      return;
    }

    try {
      await this.connection.invoke(groupMessageTypes.join, companyId, ownerId);
      this.#joinedOwners.set(ownerId, { ownerKind, companyId });
    } catch (error) {
      this.#logger.error('[AiProcessRuns SignalR] Failed to join owner group', ownerId, error);
    }
  }

  async leaveOwner(ownerKind, companyId, ownerId) {
    if (!ownerId) {
      return;
    }

    const groupMessageTypes = OWNER_GROUP_MESSAGE_TYPES[ownerKind];

    if (!groupMessageTypes) {
      this.#logger.error('[AiProcessRuns SignalR] Unsupported owner kind for leave', ownerKind);

      return;
    }

    if (!this.#connection?.isConnected) {
      this.#joinedOwners.delete(ownerId);

      return;
    }

    try {
      await this.connection.invoke(groupMessageTypes.leave, companyId, ownerId);
    } catch (error) {
      this.#logger.error('[AiProcessRuns SignalR] Failed to leave owner group', ownerId, error);
    }

    this.#joinedOwners.delete(ownerId);
  }

  async joinExecution(executionId) {
    if (!executionId) {
      return;
    }

    // Cold-load race: the drawer can fire this before the connection finishes
    // starting (state still Connecting). Rather than silently drop the join
    // (which left the open drawer frozen until a manual reload — the very bug
    // this fixes), record the id as pending so it is re-joined the moment the
    // connection settles via resubscribe() (initial start drain + reconnect +
    // wake). The leave path clears it whether it was pending or joined.
    if (!this.#connection?.isConnected) {
      this.#pendingExecutionIds.add(executionId);
      this.#logger.warn(
        '[AiProcessRuns SignalR] Deferring join execution — connection not active',
        executionId,
      );

      return;
    }

    try {
      await this.connection.invoke(AiProcessRunsSignalRMessageType.JOIN_EXECUTION, executionId);
      this.#pendingExecutionIds.delete(executionId);
      this.#joinedExecutionIds.add(executionId);
    } catch (error) {
      this.#logger.error('[AiProcessRuns SignalR] Failed to join execution', executionId, error);
    }
  }

  async leaveExecution(executionId) {
    if (!executionId) {
      return;
    }

    this.#pendingExecutionIds.delete(executionId);

    if (!this.#connection?.isConnected) {
      this.#joinedExecutionIds.delete(executionId);

      return;
    }

    try {
      await this.connection.invoke(AiProcessRunsSignalRMessageType.LEAVE_EXECUTION, executionId);
    } catch (error) {
      this.#logger.error('[AiProcessRuns SignalR] Failed to leave execution', executionId, error);
    }

    this.#joinedExecutionIds.delete(executionId);
  }

  #handleExecutionEvent(eventName) {
    return payload => {
      const execution = extractExecutionFromPayload(payload);

      if (!execution) {
        this.#logger.warn(
          '[AiProcessRuns SignalR] Dropping execution event — invalid payload',
          eventName,
        );

        return;
      }

      const nextStatus = executionStatusByEvent[eventName] ?? execution.status;
      // DEV-3325: a terminal event with no server-supplied completedAt still ends
      // the run — stamp the event arrival time so the run-detail header freezes the
      // duration instead of ticking forever (status Completed + completedAt null).
      const terminalFallbackCompletedAt = TerminalExecutionEventTypes.has(eventName)
        ? new Date().toISOString()
        : null;
      const completedAt =
        eventName === AiProcessRunsSignalREventType.EXECUTION_STARTED
          ? (execution.completedAt ?? null)
          : (execution.completedAt ?? payload?.completedAt ?? terminalFallbackCompletedAt);

      this.#dispatch(
        executionUpsertAction({
          execution: {
            ...execution,
            status: nextStatus,
            completedAt,
          },
          ownerId: resolveOwnerIdFromPayload(execution, payload),
        }),
      );
    };
  }

  #handleStepEvent(eventName) {
    return payload => {
      const step = extractStepFromPayload(payload);

      if (!step) {
        this.#logger.warn(
          '[AiProcessRuns SignalR] Dropping step event — invalid payload',
          eventName,
        );

        return;
      }

      const nextStatus = stepStatusByEvent[eventName] ?? step.status;

      this.#dispatch(
        stepUpsertAction({
          step: {
            ...step,
            status: nextStatus,
          },
        }),
      );

      // DEV-3170: the awaiting-confirmation event now also carries the pending
      // tool call so the gate can render what the reviewer is approving without
      // waiting for a REST round-trip. Guard against absence — older servers and
      // non-tool gates emit the event without these fields.
      if (
        eventName === AiProcessRunsSignalREventType.STEP_AWAITING_CONFIRMATION &&
        !_.isNil(step.toolName)
      ) {
        this.#dispatch(
          setStepPendingToolCallAction({
            stepExecutionId: step.id,
            pendingToolCall: {
              toolCallId: step.toolCallId ?? null,
              toolName: step.toolName,
              toolArguments: step.toolArguments ?? null,
              policy: step.policy ?? null,
            },
          }),
        );
      }

      // The awaiting-user-input event carries the pause envelope's questions so
      // the question card renders without a REST round-trip. Guard against
      // absence so a malformed event doesn't seed an empty card (the reload
      // fallback fetches the questions over REST when this is missing).
      if (
        eventName === AiProcessRunsSignalREventType.STEP_AWAITING_USER_INPUT &&
        _.isArray(step.questions)
      ) {
        this.#dispatch(
          setStepPendingQuestionAction({
            stepExecutionId: step.id,
            pendingQuestion: {
              tool: step.tool ?? null,
              reason: step.reason ?? null,
              questions: step.questions,
            },
          }),
        );
      }
    };
  }

  #handleMessageEvent(eventName) {
    return payload => {
      const extracted = extractMessageFromPayload(payload, eventName);

      if (!extracted) {
        this.#logger.debug?.(
          '[AiProcessRuns SignalR] Dropping message-related event — no step execution context',
          eventName,
        );

        return;
      }

      this.#dispatch(messageAppendAction(extracted));
    };
  }

  #handleFormFieldSetEvent() {
    return payload => {
      const extracted = extractFormFieldEventFromPayload(payload);

      if (!extracted) {
        this.#logger.debug?.(
          '[AiProcessRuns SignalR] Dropping form_field_set — no execution/field context',
        );

        return;
      }

      this.#dispatch(formFieldSetAction(extracted));
    };
  }

  #handleFormStatusChangedEvent() {
    return payload => {
      const extracted = extractFormStatusEventFromPayload(payload);

      if (!extracted) {
        this.#logger.debug?.(
          '[AiProcessRuns SignalR] Dropping form_status_changed — no execution/status context',
        );

        return;
      }

      this.#dispatch(formStatusChangedAction(extracted));
    };
  }

  #startListeners() {
    const executionEvents = [
      AiProcessRunsSignalREventType.EXECUTION_STARTED,
      AiProcessRunsSignalREventType.EXECUTION_COMPLETED,
      AiProcessRunsSignalREventType.EXECUTION_FAILED,
      AiProcessRunsSignalREventType.EXECUTION_CANCELLED,
      AiProcessRunsSignalREventType.EXECUTION_AWAITING_CONFIRMATION,
    ];
    const stepEvents = [
      AiProcessRunsSignalREventType.STEP_STARTED,
      AiProcessRunsSignalREventType.STEP_COMPLETED,
      AiProcessRunsSignalREventType.STEP_FAILED,
      AiProcessRunsSignalREventType.STEP_AWAITING_CONFIRMATION,
      AiProcessRunsSignalREventType.STEP_AWAITING_USER_INPUT,
    ];
    const messageEvents = [
      AiProcessRunsSignalREventType.TOOL_STARTED,
      AiProcessRunsSignalREventType.TOOL_COMPLETED,
      AiProcessRunsSignalREventType.TOOL_FAILED,
      AiProcessRunsSignalREventType.TOOL_CANCELLED,
      AiProcessRunsSignalREventType.TOOL_EXECUTED,
      AiProcessRunsSignalREventType.TOOL_CONFIRMATION_REQUESTED,
      AiProcessRunsSignalREventType.TOOL_EXECUTION_REQUESTED,
      AiProcessRunsSignalREventType.STEP_MESSAGE_APPENDED,
      AiProcessRunsSignalREventType.PLAN_CREATED,
      AiProcessRunsSignalREventType.PLAN_UPDATED,
      AiProcessRunsSignalREventType.PLAN_REVISED,
      AiProcessRunsSignalREventType.PLAN_COMPLETED,
      AiProcessRunsSignalREventType.PLAN_CANCELLED,
      AiProcessRunsSignalREventType.PLAN_STEP_STARTED,
      AiProcessRunsSignalREventType.PLAN_STEP_COMPLETED,
      AiProcessRunsSignalREventType.PLAN_STEP_FAILED,
      AiProcessRunsSignalREventType.PLAN_STEP_TOOL_DISPATCHED,
      AiProcessRunsSignalREventType.PLAN_STEP_TOOL_RETURNED,
      AiProcessRunsSignalREventType.RESPONSE_TOKEN,
    ];

    executionEvents.forEach(eventName => {
      this.connection.on(eventName, this.#handleExecutionEvent(eventName));
    });

    stepEvents.forEach(eventName => {
      this.connection.on(eventName, this.#handleStepEvent(eventName));
    });

    messageEvents.forEach(eventName => {
      this.connection.on(eventName, this.#handleMessageEvent(eventName));
    });

    this.connection.on(
      AiProcessRunsSignalREventType.FORM_FIELD_SET,
      this.#handleFormFieldSetEvent(),
    );
    this.connection.on(
      AiProcessRunsSignalREventType.FORM_STATUS_CHANGED,
      this.#handleFormStatusChangedEvent(),
    );
  }

  #stopListeners() {
    Object.values(AiProcessRunsSignalREventType).forEach(eventName => {
      this.connection.off(eventName);
    });
  }
}

export default SignalRAiProcessRunsController;
